import JSDC from '../';
import { LayerInfo } from '../Controller/LayerController';
import useLayerInfos from '../hooks/useLayerInfos';
import React, { createContext, useEffect, useRef, useState } from 'react';
import useTrailQuery from '../hooks/useTrailQuery';
import useAdvanceListingQuery from '../hooks/useAdvanceListingQuery';
import Event from '../utils/Event';
import { GeoJsonProperties } from 'geojson';
import useCluster from '../hooks/layerVisualization/useCluster';
import useHeatMap from '../hooks/layerVisualization/useHeatMap';
import JSDCGeoJSONLayer from '../Layer/JSDCGeoJSONLayer';
import { Marker } from 'leaflet';

export type ToolSettingState = { cluster: boolean; hotspot: boolean };

export type JSDCContextType = {
  Jsdc: JSDC;
  layerInfos: LayerInfo[];
  trailQuery: ReturnType<typeof useTrailQuery>;
  advanceListingQuery: ReturnType<typeof useAdvanceListingQuery>;
  trailClickEvent: Event<GeoJsonProperties>;
  trailCreateEvent: Event<JSDCGeoJSONLayer>;
  advanceCreateEvent: Event<JSDCGeoJSONLayer>;
  toolSetting: ToolSettingState;
  setToolSetting: React.Dispatch<React.SetStateAction<ToolSettingState>>;
  clusterControll: ReturnType<typeof useCluster>;
  heatMapControll: ReturnType<typeof useHeatMap>;
  openDialog: boolean;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  featureProperties: Record<string, any>;
  setFeatureProperties: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  featureColor: string;
  setFeatureColor: React.Dispatch<React.SetStateAction<string>>;
  featureIcon: string;
  setFeatureIcon: React.Dispatch<React.SetStateAction<string>>;
};

const JSDCContext = createContext<JSDCContextType>({} as JSDCContextType);

export interface IJSDCProviderProps {
  children: React.ReactNode;
  Jsdc: JSDC;
}

const JSDCProvider: React.FC<IJSDCProviderProps> = ({ Jsdc, children }) => {
  const trailClickEvent = useRef(new Event<GeoJsonProperties>()).current;
  const trailCreateEvent = useRef(new Event<JSDCGeoJSONLayer>()).current;
  const trailRemoveEvent = useRef(new Event<JSDCGeoJSONLayer>()).current;
  const advanceCreateEvent = useRef(new Event<JSDCGeoJSONLayer>()).current;
  const advanceRemoveEvent = useRef(new Event<JSDCGeoJSONLayer>()).current;
  const [toolSetting, setToolSetting] = useState<ToolSettingState>({
    cluster: true,
    hotspot: false,
  });
  const { layerInfos } = useLayerInfos(Jsdc);
  const trailQuery = useTrailQuery({
    trailClickEvent,
    trailCreateEvent,
    trailRemoveEvent,
  });
  const advanceListingQuery = useAdvanceListingQuery({
    advanceClickEvent: trailClickEvent,
    advanceCreateEvent,
    advanceRemoveEvent,
  });
  const clusterControll = useCluster(Jsdc.asyncViewer);
  const heatMapControll = useHeatMap(Jsdc.asyncViewer);
  const [openDialog, setOpenDialog] = useState(false);
  const [featureProperties, setFeatureProperties] = useState({});
  const [featureColor, setFeatureColor] = useState('#455A64');
  const [featureIcon, setFeatureIcon] = useState('la-wave-square');

  const getToolSetting = () =>
    new Promise<ToolSettingState>((resolve) => {
      setToolSetting((prev) => {
        resolve(prev);
        return prev;
      });
    });

  useEffect(() => {
    trailQuery.getAllTrails();
    advanceListingQuery.getAll();
    clusterControll.attatch(trailQuery.trailsLayer);
    clusterControll.attatch(advanceListingQuery.advanceListingLayer);
    heatMapControll.attatch(trailQuery.trailsLayer);
    heatMapControll.attatch(advanceListingQuery.advanceListingLayer);

    const syncVisualization = async (layer: JSDCGeoJSONLayer | undefined) => {
      const toolSetting = await getToolSetting();
      if (!layer) return;
      toolSetting.hotspot ? heatMapControll.setShow() : heatMapControll.setHide();
      const shouldHideTreeIcon = toolSetting.hotspot && !toolSetting.cluster;
      const apply = (l: JSDCGeoJSONLayer) =>
        shouldHideTreeIcon
          ? l.forEachLayerAsGeoJSON<Marker>((marker) => marker.setOpacity(0))
          : l.forEachLayerAsGeoJSON<Marker>((marker) => marker.setOpacity(1));
      apply(trailQuery.trailsLayer);
      apply(advanceListingQuery.advanceListingLayer);
    };

    trailCreateEvent.addEventListener(syncVisualization);
    advanceCreateEvent.addEventListener(syncVisualization);
  }, []);

  const value = {
    Jsdc,
    layerInfos,
    trailQuery,
    advanceListingQuery,
    trailClickEvent,
    trailCreateEvent,
    advanceCreateEvent,
    toolSetting,
    setToolSetting,
    clusterControll,
    heatMapControll,
    openDialog,
    setOpenDialog,
    featureProperties,
    setFeatureProperties,
    featureColor,
    setFeatureColor,
    featureIcon,
    setFeatureIcon,
  };
  return <JSDCContext.Provider value={value}>{children}</JSDCContext.Provider>;
};
JSDCProvider.displayName = 'JSDCProvider';
export { JSDCContext, JSDCProvider };
